import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ApiError,
  getIdentityIssues,
  getOpenPunches,
  getPayrollReconcile,
  getQbPushErrors,
  linkUserIdentity,
  retryQbPushes,
  type PayrollValidateResult,
} from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PayrollReconcileHelp } from "@/components/PayrollReconcileHelp";
import { fmtDateTime } from "@/lib/time";

/**
 * Admin / manager / readonly: long open punches (≥14h) and failed QB pushes.
 * Full admin: retry pushes, identity issues, current-period reconcile.
 */
export function OpsHealthCard({
  enabled,
  isAdmin = false,
}: {
  enabled: boolean;
  isAdmin?: boolean;
}) {
  const qc = useQueryClient();
  const [linkUserId, setLinkUserId] = useState<number | null>(null);
  const [linkPayrollId, setLinkPayrollId] = useState("");
  const [linkCoCode, setLinkCoCode] = useState("");
  const [linkQbEmail, setLinkQbEmail] = useState("");
  const [linkMsg, setLinkMsg] = useState<string | null>(null);

  const openQ = useQuery({
    queryKey: ["ops-open-punches"],
    queryFn: () => getOpenPunches(14),
    enabled,
    refetchInterval: 5 * 60 * 1000,
  });
  const errQ = useQuery({
    queryKey: ["ops-qb-push-errors"],
    queryFn: () => getQbPushErrors(15),
    enabled,
    refetchInterval: 5 * 60 * 1000,
  });
  const identityQ = useQuery({
    queryKey: ["ops-identity-issues"],
    queryFn: () => getIdentityIssues(30),
    enabled: enabled && isAdmin,
    refetchInterval: 10 * 60 * 1000,
  });
  const [reconcile, setReconcile] = useState<PayrollValidateResult | null>(null);

  const retryMut = useMutation({
    mutationFn: () => retryQbPushes(40),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops-qb-push-errors"] });
      void qc.invalidateQueries({ queryKey: ["ops-open-punches"] });
    },
  });
  const reconcileMut = useMutation({
    mutationFn: () => getPayrollReconcile(),
    onSuccess: (data) => setReconcile(data),
  });
  const linkMut = useMutation({
    mutationFn: () => {
      if (linkUserId == null) throw new Error("No user selected");
      return linkUserIdentity(linkUserId, {
        qb_payroll_id: linkPayrollId || undefined,
        co_code: linkCoCode || undefined,
        qb_user_email: linkQbEmail || undefined,
      });
    },
    onSuccess: () => {
      setLinkMsg("Saved identity fields.");
      setLinkUserId(null);
      setLinkPayrollId("");
      setLinkCoCode("");
      setLinkQbEmail("");
      void qc.invalidateQueries({ queryKey: ["ops-identity-issues"] });
    },
    onError: (e: Error) => setLinkMsg(e.message || "Link failed"),
  });

  const openCount = openQ.data?.count ?? 0;
  const errCount = errQ.data?.count ?? 0;
  const idCount = identityQ.data?.total_flagged ?? 0;
  const loading = openQ.isLoading || errQ.isLoading;

  function formatQueryError(err: unknown, label: string): string {
    if (err instanceof ApiError) {
      const detail =
        typeof err.detail === "object" && err.detail && "detail" in (err.detail as object)
          ? String((err.detail as { detail?: unknown }).detail ?? err.message)
          : err.message;
      if (err.status === 403) {
        return `${label}: 403 ${detail || "forbidden"}. Backend requires user.role of admin, manager, or readonly_admin (not Azure group membership alone). Check /auth/me → role.`;
      }
      if (err.status === 401) {
        return `${label}: 401 session expired or not authenticated — re-login (Entra) and retry.`;
      }
      return `${label}: HTTP ${err.status} ${detail || err.message}`;
    }
    if (err instanceof Error) return `${label}: ${err.message}`;
    return `${label}: failed to load`;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Operations health
          {!loading && openCount > 0 && (
            <Badge variant="destructive">{openCount} open ≥14h</Badge>
          )}
          {!loading && errCount > 0 && (
            <Badge variant="destructive">{errCount} QB push error{errCount === 1 ? "" : "s"}</Badge>
          )}
          {/* {isAdmin && !identityQ.isLoading && idCount > 0 && (
            <Badge variant="destructive">{idCount} identity</Badge>
          )} */}
          {!loading && openCount === 0 && errCount === 0 && !openQ.isError && !errQ.isError && (
            <Badge variant="secondary">OK</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Open punches older than 14 hours (auto-close skipped by design), failed QuickBase
          pushes (auto-retried every ~30 min; admin can force retry). Refresh every 5 minutes.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        {loading && <div className="text-muted-foreground">Loading…</div>}
        {(openQ.isError || errQ.isError) && (
          <div className="space-y-1 text-destructive text-xs break-words">
            <div className="font-medium">Failed to load ops health</div>
            {openQ.isError && <div>{formatQueryError(openQ.error, "Open punches")}</div>}
            {errQ.isError && <div>{formatQueryError(errQ.error, "QB push errors")}</div>}
          </div>
        )}

        <div>
          <div className="mb-1 font-medium">Open punches ≥ 14h ({openCount})</div>
          {openCount === 0 && !openQ.isLoading && (
            <div className="text-xs text-muted-foreground">None in scope.</div>
          )}
          {openCount > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {(openQ.data?.open_punches ?? []).slice(0, 12).map((p) => (
                <li key={p.entry_id} className="rounded border border-border px-2 py-1">
                  <span className="font-medium">{p.full_name || p.email || `entry ${p.entry_id}`}</span>
                  {" · "}
                  {p.age_hours != null ? `${p.age_hours}h` : "?"}
                  {p.recorded_time ? ` · on ${fmtDateTime(p.recorded_time, "America/Detroit").replace("America/Detroit", "ET")}` : ""}
                  {` · #${p.entry_id}`}
                  {p.site_name ? ` · ${p.site_name}` : ""}
                  {p.co_code ? ` · co ${p.co_code}` : ""}
                  {p.qb_push_status ? ` · push ${p.qb_push_status}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2 font-medium">
            <span>QB push errors ({errCount})</span>
            {isAdmin && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={retryMut.isPending}
                onClick={() => retryMut.mutate()}
              >
                {retryMut.isPending ? "Retrying…" : "Retry failed pushes"}
              </Button>
            )}
          </div>
          {retryMut.isSuccess && (
            <div className="mb-1 text-xs text-muted-foreground">
              Retry: processed {retryMut.data?.processed ?? 0}, ok {retryMut.data?.ok ?? 0}, failed{" "}
              {retryMut.data?.failed ?? 0}, skipped {retryMut.data?.skipped ?? 0}
            </div>
          )}
          {retryMut.isError && (
            <div className="mb-1 text-xs text-destructive">Retry failed (admin only).</div>
          )}
          {errCount === 0 && !errQ.isLoading && (
            <div className="text-xs text-muted-foreground">None recent.</div>
          )}
          {errCount > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {(errQ.data?.errors ?? []).map((e) => (
                <li key={e.entry_id} className="rounded border border-border px-2 py-1">
                  <span className="font-medium">{e.full_name || e.email || `entry ${e.entry_id}`}</span>
                  {e.qb_record_id ? ` · QB ${e.qb_record_id}` : " · no rid"}
                  {e.qb_push_attempts != null ? ` · tries ${e.qb_push_attempts}` : ""}
                  {e.qb_push_last_error ? (
                    <div className="mt-0.5 break-words text-muted-foreground">{e.qb_push_last_error}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* {isAdmin && (
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2 font-medium">
              <span>Payroll reconcile (current week)</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={reconcileMut.isPending}
                onClick={() => reconcileMut.mutate()}
              >
                {reconcileMut.isPending ? "Running…" : "Run reconcile"}
              </Button>
            </div>
            {reconcile && (
              <div className="space-y-1 rounded border border-border px-2 py-1.5 text-xs">
                <div>
                  {(reconcile.included_entries ?? 0) === 0 && (reconcile.candidate_entries ?? 0) > 0 ? (
                    <Badge variant="destructive">Empty ADP file</Badge>
                  ) : reconcile.ok ? (
                    <Badge variant="secondary">OK</Badge>
                  ) : (
                    <Badge variant="destructive">Issues</Badge>
                  )}{" "}
                  week {reconcile.pay_week || "?"} · {reconcile.from_date} → {reconcile.to_date}
                </div>
                <div className="text-muted-foreground">
                  <span title="Included punch hours minus Reg+OT (0 = match). Vacuous 0 when nothing was included.">
                    Δh {reconcile.duration_vs_export_delta}
                  </span>
                  {" · "}
                  <span title="ADP lines with blank Co Code (should be 0)">
                    missing co {reconcile.missing_co_code_rows}
                  </span>
                  {" · "}
                  <span title="ADP lines with blank File # cell (should be 0). Not the same as exclusion missing_payroll_file_number.">
                    missing file {reconcile.missing_file_rows}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  Punches in period:{" "}
                  <strong className="text-foreground">{reconcile.included_entries}</strong> on file
                  {" / "}
                  <strong className="text-foreground">{reconcile.candidate_entries}</strong> found
                  {(reconcile.excluded_entries ?? 0) > 0 && (
                    <>
                      {" "}
                      (
                      <span title="Live count of punches dropped this run — not a hardcoded number">
                        {reconcile.excluded_entries} not exported
                      </span>
                      )
                    </>
                  )}
                  {" · "}
                  payroll lines: {reconcile.payroll_rows}
                </div>
                {(reconcile.included_entries ?? 0) === 0 && (reconcile.candidate_entries ?? 0) > 0 && (
                  <div className="text-amber-700 dark:text-amber-400">
                    Nothing made it onto the payroll file for this week. Fix File #s and/or close open
                    punches — see breakdown below.
                  </div>
                )}
                {Object.keys(reconcile.exclusion_reason_counts || {}).length > 0 && (
                  <div className="text-muted-foreground">
                    Why not exported:{" "}
                    {Object.entries(reconcile.exclusion_reason_counts)
                      .map(([k, v]) => `${k}=${v}`)
                      .join(", ")}
                  </div>
                )}
                <PayrollReconcileHelp compact />
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div>
            <div className="mb-1 font-medium">
              Identity issues ({identityQ.data?.total_flagged ?? "…"})
            </div>
            <div className="mb-2 text-xs text-muted-foreground">
              Missing File #, co_code, or qb_user_email; login email ≠ QB email. Fix before ADP export.
            </div>
            {(identityQ.data?.users?.length ?? 0) === 0 && !identityQ.isLoading && (
              <div className="text-xs text-muted-foreground">No flagged users (sample limit).</div>
            )}
            <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
              {(identityQ.data?.users ?? []).map((u) => (
                <li key={u.user_id} className="rounded border border-border px-2 py-1">
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <span>
                      <span className="font-medium">{u.full_name || u.email}</span>
                      {u.punches_90d ? ` · ${u.punches_90d} punches/90d` : ""}
                      <span className="text-muted-foreground"> · {u.issues.join(", ")}</span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        setLinkUserId(u.user_id);
                        setLinkPayrollId(u.qb_payroll_id || "");
                        setLinkCoCode(u.co_code || "");
                        setLinkQbEmail(u.qb_user_email || u.email || "");
                        setLinkMsg(null);
                      }}
                    >
                      Fix
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            {linkUserId != null && (
              <div className="mt-2 grid gap-2 rounded border border-border p-2">
                <div className="text-xs font-medium">Link identity (user {linkUserId})</div>
                <div className="grid gap-1 sm:grid-cols-3">
                  <Input
                    className="h-8 text-xs"
                    placeholder="File # (qb_payroll_id)"
                    value={linkPayrollId}
                    onChange={(e) => setLinkPayrollId(e.target.value)}
                  />
                  <Input
                    className="h-8 text-xs"
                    placeholder="Co code"
                    value={linkCoCode}
                    onChange={(e) => setLinkCoCode(e.target.value)}
                  />
                  <Input
                    className="h-8 text-xs"
                    placeholder="QB user email"
                    value={linkQbEmail}
                    onChange={(e) => setLinkQbEmail(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={linkMut.isPending}
                    onClick={() => linkMut.mutate()}
                  >
                    {linkMut.isPending ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => setLinkUserId(null)}
                  >
                    Cancel
                  </Button>
                </div>
                {linkMsg && <div className="text-xs text-muted-foreground">{linkMsg}</div>}
              </div>
            )}
          </div>
        )} */}
      </CardContent>
    </Card>
  );
}
